import { lazy, Suspense, useEffect, useState } from 'react';
import { Code, Eye, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUpdateProductFields } from '@/hooks/useProduct';
import { HtmlSourceEditor } from './HtmlSourceEditor';

// The WYSIWYG editor pulls in TipTap (~heavy) — load it only when a product
// detail page is actually opened, keeping it out of the initial bundle.
const RichTextEditor = lazy(() => import('./RichTextEditor'));

interface ProductDescriptionCardProps {
  shopifyProductId: number;
  /** The product's current `body_html`. */
  bodyHtml: string | null;
}

/**
 * The product description editor — a Shopify-parity WYSIWYG editor over the
 * `body_html` field, with a "Show HTML" toggle. Edits are local until Save,
 * which pushes the description straight to Shopify.
 */
export function ProductDescriptionCard({
  shopifyProductId,
  bodyHtml,
}: ProductDescriptionCardProps) {
  const update = useUpdateProductFields();
  const server = bodyHtml ?? '';
  const [html, setHtml] = useState(server);
  const [showHtml, setShowHtml] = useState(false);

  // Re-seed when the product (re)loads with different saved HTML.
  useEffect(() => {
    setHtml(bodyHtml ?? '');
  }, [bodyHtml]);

  const dirty = html !== server;

  const save = () => {
    update.mutate(
      { shopifyProductId, fields: { body_html: html } },
      {
        onSuccess: () => toast.success('Description saved to Shopify'),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Description</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={() => setShowHtml((v) => !v)}
        >
          {showHtml ? (
            <>
              <Eye className="h-3.5 w-3.5" />
              Rich text
            </>
          ) : (
            <>
              <Code className="h-3.5 w-3.5" />
              Show HTML
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {showHtml ? (
          <HtmlSourceEditor value={html} onChange={setHtml} />
        ) : (
          <Suspense
            fallback={
              <div
                className="flex h-56 items-center justify-center rounded-md border border-input bg-background"
                aria-busy="true"
              >
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <RichTextEditor value={html} onChange={setHtml} />
          </Suspense>
        )}

        {dirty && (
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHtml(server)}
              disabled={update.isPending}
            >
              Discard
            </Button>
            <Button size="sm" className="gap-1.5" onClick={save} disabled={update.isPending}>
              {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save description
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
